/**
 * Smooth-scroll to an in-page anchor and strip the hash from the URL so a
 * subsequent page reload starts at the top instead of jumping to the
 * mid-page anchor. External deep-links (`coresmith.dev/#expertise`) still
 * work — the hash is in the URL when the page loads, the cross-page hash
 * handler in App.tsx scrolls to it, and only THEN does this strip it.
 */
export const scrollToHash = (hash: string): void => {
  const id = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!id) return;
  const target = document.getElementById(id);
  if (!target) return;
  target.scrollIntoView({ behavior: "smooth", block: "start" });
  // Wait one frame so the smooth-scroll has visibly started before we
  // mutate the URL — replaceState is silent but feels less abrupt this way.
  requestAnimationFrame(() => {
    if (window.location.hash) {
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    }
  });
};

/**
 * Click handler for in-page hash anchors. Drop-in for `<a href="#foo">`:
 *
 *   <a href="#roi-calculator" onClick={handleHashClick}>...
 *
 * Doesn't interfere with external links, modified clicks (cmd+click to
 * open in new tab), or links that don't start with `#`.
 */
export const handleHashClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
  // Let the browser handle modified clicks (open in new tab, etc.)
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
  const href = e.currentTarget.getAttribute("href") || "";
  if (!href.startsWith("#")) return;
  e.preventDefault();
  scrollToHash(href);
};
